import { Component } from 'react';
import { connect } from 'react-redux';
import { Segment } from '../core/core';
import MyPeekavilleBanner from '../TeacherLibrary/MyPeekavilleBanner';
import LibraryCard from '../TeacherLibrary/LibraryCard';
import LockConfirmModal from './LockConfirmModal';
import { retrievePeekavilleUnitLocks, storePeekavilleUnitLocks } from '../BackendInterface';
import { canAccessPeekaville, getUnitTitleForDisplay } from '../GlobalFunctions';
import { getElementarySchoolUnits } from '../components/LibraryCard/cardData';
import { SpinnerOverlay } from '../SpinnerOverlay';
import PrimaryButton from '../components/PrimaryButton/PrimaryButton';
import { selectClassroom, updateClassroom } from '../Classrooms';
import cx from 'classnames';
import styles from '../SCSS/MyPeekavilleUnits.module.scss';
import utilityStyles from '../SCSS/common/utilityStyles.module.scss';
import segmentStyles from '../SCSS/Segment.module.scss';
import type { ClassroomType, MyPeekavilleUnitLocksObjectType, SessionType, UserProfileType } from './../types';
import type { ElementaryCard } from '../components/LibraryCard/cardData';
type Props = {
  session: SessionType;
  classrooms: {
    list: Array<ClassroomType>;
    selectedClassroom: number;
  };
  classroom: ClassroomType;
  updateClassroom: (arg0: Record<string, unknown>) => void;
  router: (arg0: string) => void;
  isAuthenticated: boolean;
  userProfile: UserProfileType;
  history: {
    push: (arg0: string) => void;
  };
};
type State = {
  unitLocked: Array<boolean>;
  isShowLockConfirmModal: boolean;
  actionLock: number | boolean | void;
  // 0: neutral, 1: false, 2:true
  currLC: number;
  classroomId: string;
  isShowSpinner: boolean;
  classroomUnderEdit: ClassroomType | void;
};

class MyPeekavilleUnits extends Component<Props, State> {
  keys: number[];
  unitNames: string[];
  units: ElementaryCard[];
  constructor(props: Props) {
    super(props);
    const editableClassroom = { ...props.classroom };
    // Get only units with Peekaville content, filtered by user's content tags (MENA vs non-MENA)
    this.units = getElementarySchoolUnits().filter((u) => u.hasPeekavilleContent);
    // Extract unit names with proper MENA suffixes
    this.unitNames = this.units.map((d) => d.unit);
    this.keys = this.unitNames
      .map(function (x, i) {
        return i;
      })
      .reverse();
    this.state = {
      unitLocked: this.unitNames.map(function (x, i) {
        return true;
      }),
      isShowLockConfirmModal: false,
      actionLock: undefined,
      currLC: -1,
      isShowSpinner: false,
      classroomUnderEdit: editableClassroom,
      classroomId: editableClassroom.classroomId,
    };
    this.handleOnLockAllUnits = this.handleOnLockAllUnits.bind(this);
    this.handleOnUnlockAllUnits = this.handleOnUnlockAllUnits.bind(this);
    this.handleOnStudents = this.handleOnStudents.bind(this);
    this.callbackOnLockUnit = this.callbackOnLockUnit.bind(this);
    this.callbackOnUnlockUnit = this.callbackOnUnlockUnit.bind(this);
    this.callbackOnConfirmLock = this.callbackOnConfirmLock.bind(this);
    this.callbackOnConfirmUnlock = this.callbackOnConfirmUnlock.bind(this);
    this.handleOnHideLockConfirmModal = this.handleOnHideLockConfirmModal.bind(this);
    this.peekavilleUnitLocksToState = this.peekavilleUnitLocksToState.bind(this);
    this.saveUnitLocks = this.saveUnitLocks.bind(this);
  }

  UNSAFE_componentWillMount() {
    this.initializeData(this.props);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.classroom !== prevProps.classroom) {
      this.initializeData(this.props);
    }
  }

  peekavilleUnitLocksToState(peekavilleUnitLocks: Record<string, unknown>) {
    const unitLocked = [];

    // Check if this is a MENA user based on unit names
    const isMenaUser = this.unitNames.some((name) => name.endsWith('Mena'));

    this.unitNames.forEach(function (element) {
      // Convert hyphenated name to space-separated for lookup
      const unitKey = element.replace(/-/g, ' ');

      // First try the exact unit key (for properly stored data)
      let lockValue = peekavilleUnitLocks[unitKey];

      // If not found and this is a MENA user with MENA unit names,
      // try the legacy non-MENA key
      if (lockValue === undefined && isMenaUser && element.endsWith('Mena')) {
        const baseUnit = element.replace('Mena', '').replace(/-/g, ' ');
        // "Self-RegulationMena" → "Self Regulation" for legacy data lookup
        lockValue = peekavilleUnitLocks[baseUnit];
      }

      // Default to locked if not found
      unitLocked.push(lockValue ?? true);
    });
    this.setState({
      unitLocked: unitLocked,
    });
  }

  initializeData(props: Props) {
    const { classroom } = props;
    const classroomId = classroom?.classroomId;
    const editableClassroom: ClassroomType = { ...classroom };
    this.setState(
      {
        classroomUnderEdit: editableClassroom,
        classroomId: classroomId,
        isShowSpinner: true,
      },
      () => {
        retrievePeekavilleUnitLocks(
          classroomId,
          (resultObject) => {
            const locksObject = resultObject as unknown as {
              peekavilleUnitLocks: MyPeekavilleUnitLocksObjectType;
            };
            const peekavilleUnitLocks = locksObject.peekavilleUnitLocks;
            // No longer need manual transformation - handled in peekavilleUnitLocksToState
            this.peekavilleUnitLocksToState(peekavilleUnitLocks);
            this.setState({
              isShowSpinner: false,
            });
          },
          (errorObject) => {
            console.error(errorObject);
          },
        );
      },
    );
  }

  unitStateToPeekavilleUnitLocksObject(unitLocked: Array<boolean>): MyPeekavilleUnitLocksObjectType {
    const peekavilleUnitLocks = {};
    this.unitNames.forEach(function (element, index) {
      if (unitLocked[index] !== undefined) {
        // Convert to space-separated format for backend
        // "Self-RegulationMena" → "Self RegulationMena" (MENA users)
        // "Self-Regulation" → "Self Regulation" (non-MENA users)
        const unitKey = element.replace(/-/g, ' ');
        peekavilleUnitLocks[unitKey] = unitLocked[index];
      }
    });
    return peekavilleUnitLocks;
  }

  saveUnitLocks(unitLocked: Array<boolean>) {
    this.setState({
      isShowSpinner: true,
    });
    const peekavilleUnitLocks = this.unitStateToPeekavilleUnitLocksObject(unitLocked);
    storePeekavilleUnitLocks(
      this.state.classroomId,
      peekavilleUnitLocks,
      () => {
        // Instead of directly setting state, re-fetch to ensure proper mapping
        retrievePeekavilleUnitLocks(
          this.state.classroomId,
          (resultObject) => {
            const locksObject = resultObject as unknown as {
              peekavilleUnitLocks: MyPeekavilleUnitLocksObjectType;
            };
            this.peekavilleUnitLocksToState(locksObject.peekavilleUnitLocks);
            this.setState({
              isShowSpinner: false,
            });
          },
          (errorObject) => {
            console.error(errorObject);
            this.setState({ isShowSpinner: false });
          },
        );

        // Keep the classroom store update
        const modifyClassroom = { ...this.props.classroom };
        modifyClassroom.peekavilleUnitLocks = peekavilleUnitLocks;
        this.props.updateClassroom(modifyClassroom);
      },
      (errorObject1) => {
        console.error(errorObject1);
        this.setState({ isShowSpinner: false });
      },
    );
  }

  lockAllUnits() {
    const unitLocked = this.unitNames.map(() => true);
    this.saveUnitLocks(unitLocked);
  }

  unlockAllUnits() {
    const unitLocked = this.unitNames.map(() => false);
    this.saveUnitLocks(unitLocked);
  }

  handleOnLockAllUnits() {
    this.setState({
      isShowLockConfirmModal: true,
      actionLock: true,
    });
  }

  handleOnUnlockAllUnits() {
    this.setState({
      isShowLockConfirmModal: true,
      actionLock: false,
    });
  }

  handleOnStudents() {
    this.props.history.push('/students');
  }

  callbackOnLockUnit(index: number) {
    this.setState({
      isShowLockConfirmModal: true,
      actionLock: true,
      currLC: index,
    });
  }

  callbackOnUnlockUnit(index: number) {
    this.setState({
      isShowLockConfirmModal: true,
      actionLock: false,
      currLC: index,
    });
  }

  toggleUnit(value: boolean) {
    // true for lock, false for unlock
    if (this.state.currLC !== -1) {
      const tempState = this.state.unitLocked.slice();
      tempState[this.state.currLC] = value;
      this.saveUnitLocks(tempState);
    } else {
      value ? this.lockAllUnits() : this.unlockAllUnits();
    }

    this.handleOnHideLockConfirmModal();
  }

  callbackOnConfirmLock() {
    this.toggleUnit(true);
  }

  callbackOnConfirmUnlock() {
    this.toggleUnit(false);
  }

  handleOnHideLockConfirmModal() {
    this.setState({
      isShowLockConfirmModal: false,
      actionLock: undefined,
      currLC: -1,
    });
  }

  render() {
    if (!this.props.isAuthenticated) {
      return <SpinnerOverlay isShow={true} />;
    }

    return (
      <div className={`${utilityStyles.clearfix} ${styles.MyPeekavilleUnits}`}>
        <SpinnerOverlay isShow={this.state.isShowSpinner} />
        <Segment>
          <MyPeekavilleBanner
            {...this.props}
            canAccessPeekaville={canAccessPeekaville(
              this.props.userProfile.licenseLevel,
              this.props.userProfile.licenseExpires,
            )}
            unit='Getting-Started'
          />
        </Segment>
        <Segment>
          <div className={styles.titleLine}>
            <div className={cx(segmentStyles.moduleTitle, styles.title)}>myPeekaville Units</div>
            <div className={styles.oneLineButtons}>
              <PrimaryButton
                className={styles.myPeekavilleUnitsButton}
                small
                id='lockAllUnits'
                onClick={() => {
                  this.handleOnLockAllUnits();
                }}
                ref={(input) => (this.hintTargetRef = input)}
              >
                Lock All
              </PrimaryButton>
              <PrimaryButton
                className={styles.myPeekavilleUnitsButton}
                small
                id='unlockAllUnits'
                onClick={() => {
                  this.handleOnUnlockAllUnits();
                }}
                ref={(input) => (this.hintTargetRef = input)}
              >
                Unlock All
              </PrimaryButton>
              <PrimaryButton
                className={styles.myPeekavilleUnitsButton}
                small
                id='connectParentsButton'
                onClick={() => {
                  this.handleOnStudents();
                }}
                ref={(input) => (this.hintTargetRef = input)}
              >
                Students
              </PrimaryButton>
            </div>
          </div>
        </Segment>
        <Segment className={styles.libraryCardContainer}>
          <>
            {this.units
              .map((unit, index) => {
                const unitProps = {
                  key: unit.unit,
                  unit: unit.unit,
                  displayName: unit.displayName || getUnitTitleForDisplay(unit.unit),
                  content: unit.content,
                  gradeSpan: unit.gradeSpan,
                  hasSpanishContent: unit.hasSpanishContent,
                };
                return (
                  <LibraryCard
                    {...this.props}
                    {...unitProps}
                    cleanView={true}
                    locked={this.state.unitLocked[index] === undefined ? true : this.state.unitLocked[index]}
                    disableUnit={this.state.unitLocked[index]}
                    callbackOnUnlockUnit={this.callbackOnUnlockUnit}
                    callbackOnLockUnit={this.callbackOnLockUnit}
                    cardPosition={index}
                  />
                );
              })
              .filter((n) => n)}
          </>
        </Segment>
        <LockConfirmModal
          onConfirmLock={this.callbackOnConfirmLock}
          onConfirmUnlock={this.callbackOnConfirmUnlock}
          onHide={this.handleOnHideLockConfirmModal}
          actionLock={this.state.actionLock}
          show={this.state.isShowLockConfirmModal}
          all={this.state.currLC === -1}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  classrooms: {
    state: state.classrooms.state,
    list: state.classrooms.classrooms,
    selectedClassroom: state.classrooms.selectedClassroom,
  },
  classroom: selectClassroom(state),
  userProfile: state.user.userProfile,
});

const mapDispatchToProps = (dispatch) => ({
  updateClassroom: (classroom) => dispatch(updateClassroom(classroom)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MyPeekavilleUnits);
